import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import authService from '../services/authService';

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [isLoading, setIsLoading] = useState(true);
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  const [isRegistering, setIsRegistering] = useState(false);
  const [isGustRegistering, setIsGustRegistering] = useState(false);
  const [isGustLoggingIn, setIsGustLoggingIn] = useState(false);
  const [isVerifyingOtp, setIsVerifyingOtp] = useState(false);
  const navigate = useNavigate();

  const logout = useCallback(() => {
    localStorage.removeItem('token');
    setToken(null);
    setUser(null);
    navigate('/login-page', { replace: true });
  }, [navigate]);

  useEffect(() => {
    const verifyToken = async () => {
      try {
        if (token) {
          const userData = await authService.getMe(token);
          setUser(userData);
        }
      } catch (error) {
        console.error('Token verification failed:', error.response?.status, error.message);
        if (error.response?.status === 401) {
          // Only logout on explicit unauthorized error
          logout();
        }
      } finally {
        setIsLoading(false);
      }
    };
  
    verifyToken();
  }, [token, logout]);

  const register = async (userData) => {
    setIsRegistering(true);
    try {
      const response = await authService.register(userData);
      navigate('/verify-otp', { state: { email: userData.email } });
      return response;
    } catch (error) {
      console.error('Registration failed:', error);
      throw error;
    } finally {
      setIsRegistering(false);
    }
  };
  const gustRegister = async (userData) => {
    setIsGustRegistering(true);
    try {
      const response = await authService.gustUser(userData);
      navigate('/verify-otp', { state: { email: userData.email } });
      return response;
    } catch (error) {
      console.error('Registration failed:', error);
      throw error;
    } finally {
      setIsGustRegistering(false);
    }
  };

  const gustLogin = async (userData) => {
    setIsGustLoggingIn(true);
    try {
      const response = await authService.gustLogin(userData);
      if (response.requiresVerification) {
        navigate('/verify-otp', { state: { email: userData.email } });
        return response;
      } else {
        const { token, user } = response;
        localStorage.setItem('token', token);
        setToken(token);
        setUser(user);
        navigate('/guestDashboard', { replace: true });
      }
      return response;
    } catch (error) {
      console.error('Guest login failed:', error);
      throw error;
    } finally {
      setIsGustLoggingIn(false);
    }
  };

  const verifyOtp = async (email, otp) => {
    setIsVerifyingOtp(true);
    try {
      const response = await authService.verifyOtp(email, otp);
      //Only guest users (user_type == 1) go to dashboard, others go to login
      if (response.user && response.user.user_type === 1) {
        // Set both user and token for guest users
        if (response.access_token) {
          localStorage.setItem('token', response.access_token);
          setToken(response.access_token);
        }
        setUser(response.user);
        navigate('/guestDashboard', { state: { email: response.user.email } });
      }else if(response.user && response.user.user_type === 0){
        // Set both user and token for regular users
        if (response.access_token) {
          localStorage.setItem('token', response.access_token);
          setToken(response.access_token);
        }
        setUser(response.user);
        navigate('/dashboard', { state: { email: response.user.email } });
      } else {
        setUser(response.user);
        navigate('/login-page');
      }
      // setUser(response.user);
      // navigate('/login-page')
    } catch (error) {
      console.error('OTP verification failed:', error);
      throw error;
    } finally {
      setIsVerifyingOtp(false);
    }
  };

  const login = async (credentials) => {
    setIsLoggingIn(true);
    try {
      const response = await authService.login(credentials);
      
      // Check if email verification is required
      if (response.requiresVerification) {
        console.log(response);
        navigate('/verify-otp', { state: { email: response.email } });
        return response;
      } else {
        const { token, user } = response;
        localStorage.setItem('token', token);
        setToken(token);
        setUser(user);
        navigate('/dashboard', { replace: true });
      }
      
    } catch (error) {
      console.error('Login failed:', error);
      throw error;
    } finally {
      setIsLoggingIn(false);
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        token,
        isLoading,
        isLoggingIn,
        isRegistering,
        isVerifyingOtp,
        register,
        verifyOtp,
        login,
        logout,
        gustRegister,
        gustLogin,
        isGustRegistering,
        isGustLoggingIn,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};